import { useEffect, useState } from "react"

import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import toastr from "toastr"

import { SuccessPlaceholder } from "pages/success/success-placeholder"

import { useLocalStorage } from "hooks/utils/use-local-storage"
import { useTicketContext } from "contexts/ticket-context"
import { useBaseContext } from "contexts/use-base-context"
import { useRegistrationData } from "hooks/api/details/use-registration-data"
import { useEventData } from "hooks/api/details/use-event-data"
import { useOrderData } from "hooks/api/order/use-order-data"
import { useCompleteOrderMutation } from "hooks/api/order/use-complete-order-mutation"

import { getNavigationPath } from "utils/navigation"

const Success = () => {
  const { uuid } = useParams()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const orderIdParam = searchParams.get("order_id")
  const [orderId, setOrderId] = useState<string | null>(null)
  const { setLocalStorageData } = useLocalStorage()
  const { setTicketSelections } = useTicketContext()
  const isPreview = window.location.search.includes("isPreview=true")
  const { bundleUUID, refetchRegistrationData } = useRegistrationData()
  const { currencySymbol } = useBaseContext()
  const deviceId = localStorage.getItem("device_id") || ""
  const { data: eventData, isPending: isEventDataPending } =
    useEventData(bundleUUID)

  const {
    orderData: orderDetails,
    isPending: isOrderDetailsPending,
    error: orderError,
  } = useOrderData({
    orderUuid: orderId || "",
    deviceId,
    enabled: !!orderId,
  })

  const completeOrderMutation = useCompleteOrderMutation({
    onSuccess: (orderId) => {
      setOrderId(orderId)
    },
    onCleanup: () => {
      setLocalStorageData(null)
      setTicketSelections([])
    },
  })

  const handleMoreTickets = () => {
    refetchRegistrationData()
    navigate(getNavigationPath(`/${uuid}/select-tickets`, isPreview))
  }

  // Handle order data fetch errors
  useEffect(() => {
    if (orderError) {
      toastr.error("Failed to fetch order details")
      navigate(getNavigationPath(`/${uuid}/order-not-found`, isPreview))
    }
  }, [orderError, navigate, uuid, isPreview])

  useEffect(() => {
    if (orderIdParam) completeOrderMutation.mutate({ orderId: orderIdParam })
  }, [orderIdParam])

  useEffect(() => {
    if (orderDetails && !isOrderDetailsPending) {
      // Create an element for screen readers to announce the order completion
      const statusElement = document.createElement("div")
      statusElement.setAttribute("role", "status")
      statusElement.setAttribute("aria-live", "assertive")
      statusElement.className = "sr-only"
      statusElement.textContent = `${t("success.order_complete")} ${t(
        "success.order_number"
      )} ${orderDetails.id}`
      document.body.appendChild(statusElement)

      // Remove after announcement
      setTimeout(() => {
        document.body.removeChild(statusElement)
      }, 1000)
    }
  }, [orderDetails, isOrderDetailsPending, t])

  // Format price from cents to currency unit
  const formatPrice = (priceInCents: number) => {
    return (priceInCents / 100).toFixed(2)
  }

  // Helper function to format discount information
  const getDiscountDisplay = () => {
    if (!orderDetails?.discount_code) return null

    const { code, percent_off, amount_off } = orderDetails.discount_code

    if (percent_off) {
      const percentValue =
        typeof percent_off === "string" ? parseFloat(percent_off) : percent_off
      return {
        code,
        displayText: `(${percentValue}% OFF)`,
        amount: ((orderDetails.total * percentValue) / 100).toFixed(2),
      }
    }

    if (amount_off) {
      return {
        code,
        displayText: "",
        amount: amount_off.toFixed(2),
      }
    }

    return { code, displayText: "", amount: "0.00" }
  }

  // Get discount information if available
  const discountInfo = getDiscountDisplay()
  const hasDiscount = !!discountInfo

  if (completeOrderMutation.isPending) {
    return (
      <div className="container px-4 py-8 mx-auto">
        <div className="text-center" role="status" aria-live="polite">
          <h2 className="mb-4 text-2xl font-bold">
            {t("general.processing")}...
          </h2>
          <p className="sr-only">{t("success.processing_order")}</p>
        </div>
      </div>
    )
  }

  if (isOrderDetailsPending || isEventDataPending) {
    return (
      <SuccessPlaceholder
        aria-label={t("general.loading", "Loading content")}
      />
    )
  }

  if (isPreview) {
    window.parent.postMessage({ action: "REFRESH_LIST" }, "*")
  }

  return (
    <div className="container px-4 py-8 mx-auto">
      <div className="flex flex-col gap-8 md:flex-row">
        <div className="flex-1">
          <div
            className="bg-white rounded-lg border border-progress_bar_background text-default_card_text"
            role="region"
            aria-label={t("success.order_confirmation")}
          >
            <div className="flex flex-col gap-2 items-start p-6">
              <h2
                className="mb-4 text-3xl font-bold text-black"
                id="confirmation-heading"
              >
                {`${t("success.thank_you")} #${orderDetails?.id}`}
              </h2>
              <p className="mb-4 text-lg text-gray-600">
                {t("success.check_email")}
              </p>

              <div className="mb-8 md:hidden">
                <h3 className="mb-4 font-semibold" id="mobile-summary-heading">
                  {t("general.order_summary")}
                </h3>
                <div
                  className="space-y-2"
                  role="region"
                  aria-labelledby="mobile-summary-heading"
                >
                  <ul className="p-0 m-0 list-none">
                    {orderDetails?.ticket_set &&
                      Object.entries(
                        orderDetails.ticket_set.reduce(
                          (acc: { [key: string]: any }, ticket: any) => {
                            const typeName =
                              ticket.ticket_type || ticket.ticket_name
                            if (!acc[typeName]) {
                              acc[typeName] = {
                                count: 1,
                                price: ticket.price,
                                name: ticket.ticket_name,
                              }
                            } else {
                              acc[typeName].count += 1
                            }
                            return acc
                          },
                          {}
                        )
                      ).map(([name, details]: [string, any]) => (
                        <li key={name} className="flex justify-between mb-2">
                          <span
                            aria-label={`${details.count} ${details.name} tickets`}
                          >
                            {details.count} x {details.name}
                          </span>
                          <span
                            aria-label={`${currencySymbol}${formatPrice(
                              details.price * details.count
                            )}`}
                          >
                            {currencySymbol}
                            {formatPrice(details.price * details.count)}
                          </span>
                        </li>
                      ))}
                  </ul>

                  <div className="pt-4 border-t border-progress_bar_background">
                    {/* Subtotal */}
                    <div className="flex justify-between mb-2">
                      <span className="text-black" id="mobile-subtotal-heading">
                        {t("general.subtotal")}
                      </span>
                      <span
                        className="text-black"
                        aria-labelledby="mobile-subtotal-heading"
                      >
                        {currencySymbol}
                        {formatPrice(orderDetails?.amount_subtotal || 0)}
                      </span>
                    </div>

                    {/* Discount if applicable */}
                    {hasDiscount && (
                      <div className="flex justify-between mb-2 text-red-500">
                        <div className="flex items-center">
                          <span>{discountInfo.code}</span>
                          {discountInfo.displayText && (
                            <span className="ml-1">
                              {discountInfo.displayText}
                            </span>
                          )}
                        </div>
                        <span>
                          -{currencySymbol}
                          {formatPrice(orderDetails?.amount_discount || 0)}
                        </span>
                      </div>
                    )}

                    {/* Tax */}
                    {orderDetails?.amount_tax_paid !== undefined && (
                      <div className="flex justify-between mb-4">
                        <span>{t("general.sales_tax")}</span>
                        <span>
                          {currencySymbol}
                          {formatPrice(orderDetails.amount_tax_paid || 0)}
                        </span>
                      </div>
                    )}

                    {/* Total */}
                    <div className="flex justify-between pt-2 font-semibold border-t border-progress_bar_background">
                      <span id="mobile-total-heading">
                        {t("general.total")}
                      </span>
                      <span aria-labelledby="mobile-total-heading">
                        {currencySymbol}
                        {orderDetails?.total
                          ? formatPrice(orderDetails.total)
                          : "0.00"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <a
                href={`mailto:${eventData?.results[0]?.contact_email}`}
                className="inline-flex px-4 py-2 rounded-md border border-progress_bar_background hover:bg-gray-50"
                aria-label={`${t("general.contact")} ${
                  eventData?.results[0]?.event_name ||
                  t("general.private_admin")
                }`}
              >
                {t("general.contact")}
              </a>
              <button
                onClick={handleMoreTickets}
                className="px-6 py-3 mb-8 rounded-md bg-theme_accent text-theme_accent_contrast hover:bg-theme_accent_light"
                aria-labelledby="more-tickets-heading"
              >
                {t("success.register_more")}
              </button>
            </div>
          </div>
        </div>

        <div className="hidden w-80 md:block">
          <div className="sticky top-8 p-6 bg-white rounded-lg border border-progress_bar_background text-default_card_text">
            <h3
              className="mb-4 text-2xl font-semibold text-black"
              id="desktop-summary-heading"
            >
              {t("general.order_summary")}
            </h3>
            <div
              className="space-y-2"
              role="region"
              aria-labelledby="desktop-summary-heading"
            >
              <ul className="p-0 m-0 list-none">
                {orderDetails?.ticket_set &&
                  Object.entries(
                    orderDetails.ticket_set.reduce(
                      (acc: { [key: string]: any }, ticket: any) => {
                        const typeName =
                          ticket.ticket_type || ticket.ticket_name
                        if (!acc[typeName]) {
                          acc[typeName] = {
                            count: 1,
                            price: ticket.price,
                            name: ticket.ticket_name,
                          }
                        } else {
                          acc[typeName].count += 1
                        }
                        return acc
                      },
                      {}
                    )
                  ).map(([name, details]: [string, any]) => (
                    <li key={name} className="flex justify-between mb-2">
                      <span
                        aria-label={`${details.count} ${details.name} tickets`}
                      >
                        {details.count} x {details.name}
                      </span>
                      <span
                        aria-label={`${currencySymbol}${formatPrice(
                          details.price * details.count
                        )}`}
                      >
                        {currencySymbol}
                        {formatPrice(details.price * details.count)}
                      </span>
                    </li>
                  ))}
              </ul>

              <div className="pt-4 border-t border-progress_bar_background">
                {/* Subtotal */}
                <div className="flex justify-between mb-4" aria-live="polite">
                  <h3 className="text-black" id="desktop-subtotal-heading">
                    {t("general.subtotal")}
                  </h3>
                  <div
                    className="text-black"
                    aria-labelledby="desktop-subtotal-heading"
                  >
                    {currencySymbol}
                    {formatPrice(orderDetails?.amount_subtotal || 0)}
                  </div>
                </div>

                {/* Discount if applicable */}
                {hasDiscount && (
                  <div className="flex justify-between mb-4 text-red-500">
                    <div className="flex items-center">
                      <span className="font-medium">{discountInfo.code}</span>
                      {discountInfo.displayText && (
                        <span className="ml-1">{discountInfo.displayText}</span>
                      )}
                    </div>
                    <span>
                      -{currencySymbol}
                      {formatPrice(orderDetails?.amount_discount || 0)}
                    </span>
                  </div>
                )}

                {/* Tax */}
                {orderDetails?.amount_tax_paid !== undefined && (
                  <div className="flex justify-between mb-4">
                    <span>{t("general.sales_tax")}</span>
                    <span>
                      {currencySymbol}
                      {formatPrice(orderDetails.amount_tax_paid || 0)}
                    </span>
                  </div>
                )}

                {/* Total */}
                <div className="flex justify-between pt-2 mt-2 font-semibold border-t border-progress_bar_background">
                  <span
                    className="text-2xl font-bold text-black"
                    id="desktop-total-heading"
                  >
                    {t("general.total")}
                  </span>
                  <span
                    className="text-2xl font-bold text-black"
                    aria-labelledby="desktop-total-heading"
                  >
                    {currencySymbol}
                    {orderDetails?.total
                      ? formatPrice(orderDetails.total)
                      : "0.00"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export { Success }
